import { pb } from './pb'

/** Matches the `ingredients` JSON the app writes (`recipes/RecipesApi.kt`). */
export interface RecipeIngredient {
  amount: string
  unit: string
  name: string
}

export interface Recipe {
  id: string
  title: string
  status: string
  rejection_feedback?: string
  verified: boolean
  clean_verified: boolean
  sabbath_prep: boolean
  ingredients: RecipeIngredient[] | null
  instructions: string
  servings: number
  prep_minutes: number
  cook_minutes: number
  difficulty: string
  categories: string | string[] | null
  calories_per_serving: number
  protein_grams: number
  carbs_grams: number
  fat_grams: number
  fiber_grams: number
  created: string
  expand?: { contributor?: { id: string; name: string } }
}

/**
 * Nutrition and timing are contributor-entered and every field is optional, but PocketBase
 * returns 0 for an unset number rather than null. So 0 means "she did not say", not "zero
 * minutes". Printing "0 cal" would be inventing a fact she never gave.
 */
export function orNull(value: number | undefined): number | null {
  return value && value > 0 ? value : null
}

export function contributorName(recipe: Recipe): string {
  return recipe.expand?.contributor?.name?.trim() || 'A sister'
}

export function categoryList(recipe: Recipe): string[] {
  const raw = recipe.categories
  if (Array.isArray(raw)) return raw.filter(Boolean)
  if (typeof raw === 'string' && raw.trim()) return [raw.trim()]
  return []
}

export function totalMinutes(recipe: Recipe): number | null {
  const total = (recipe.prep_minutes ?? 0) + (recipe.cook_minutes ?? 0)
  return total > 0 ? total : null
}

/**
 * Approved recipes, newest first.
 *
 * The list rule already filters to approved plus her own plus moderators', so this asks for
 * approved explicitly rather than relying on the rule to do double duty: a woman browsing the
 * shared collection should not find her own pending submission mixed into it.
 */
export async function listApproved(): Promise<Recipe[]> {
  const result = await pb.collection('recipes').getList<Recipe>(1, 60, {
    filter: 'status = "approved"',
    sort: '-created',
    expand: 'contributor',
    // Opt out of the SDK's auto-cancellation. These two lists load together on the same screen
    // and a remount must not cancel the first one out from under the page.
    requestKey: null,
  })
  return result.items
}

/**
 * Her own recipes, whatever their status.
 *
 * Without this a submission disappears the moment she sends it: it is pending, so it is not in
 * the shared list, and there was nowhere else to look. A contributor should be able to see that
 * her recipe exists, that it is waiting, and, if it came back, what she was asked to change.
 */
export async function listMine(userId: string): Promise<Recipe[]> {
  const result = await pb.collection('recipes').getList<Recipe>(1, 60, {
    filter: `contributor = "${userId}"`,
    sort: '-created',
    expand: 'contributor',
    // Opt out of the SDK's auto-cancellation. These two lists load together on the same screen
    // and a remount must not cancel the first one out from under the page.
    requestKey: null,
  })
  return result.items
}

/**
 * Free-text match across the things a woman actually remembers about a recipe: what it was
 * called, who brought it, and what went into it.
 */
export function matchesQuery(recipe: Recipe, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const haystack = [
    recipe.title,
    contributorName(recipe),
    ...(recipe.ingredients ?? []).map((item) => item.name),
    ...categoryList(recipe),
  ]
    .join(' ')
    .toLowerCase()
  return haystack.includes(needle)
}
